import TaskHeader from "components/task/header/TaskHeader";
import { Box, Stack } from "@mui/joy";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import ContentTemplateV2 from "components/task/content/ContentTemplateV2";
import ListButton from "../../components/common/ListButton";
import { RightArrowSvg } from "../../static/image/common/SvgAssets";
import React from "react";
import useOpenTask from "hooks/useOpenTask";
import useSubscriptionStatus from "hooks/useSubscriptionStatus";
import { isWithinDaysFromNow } from "../../utils/timeUtils";
import useOpenPlanUpgrade from "../../hooks/useOpenPlanUpgrade";

// Format date to Korean format: YYYY년 MM월 DD일
const formatDateToKorean = (dateString: string): string => {
  if (!dateString) return "";

  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // getMonth() returns 0-11
  const day = date.getDate();

  return `${year}년 ${month}월 ${day}일`;
};

export default function PaymentHistoryPage() {
  const user = useRecoilValue(userAtom);
  const openPayment = useOpenTask({ taskKey: "payment" });
  const openPlanUpgradePage = useOpenPlanUpgrade();

  const subscriptionStatus = useSubscriptionStatus();
  const isExpired = subscriptionStatus?.isExpired || false;
  const expiryDaysLeft = subscriptionStatus?.expiryDaysLeft;

  const isExtensionButtonShown =
    user?.pricingModel !== "c" &&
    expiryDaysLeft !== undefined &&
    expiryDaysLeft < 21;

  const isPlanUpgradeButtonShown =
    user?.pricingModel === "aeg" &&
    user?.plan !== "master" &&
    !!subscriptionStatus?.isSubscribing &&
    subscriptionStatus?.latestPaidAt &&
    !subscriptionStatus?.hasBeenPlanUpgraded &&
    isWithinDaysFromNow(subscriptionStatus?.latestPaidAt, 14);

  const isPricingModelC = user?.pricingModel === "c";

  return (
    <>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <TaskHeader
          key={`payment-history_${user?.userId}`}
          taskKey={`payment-history-${user?.userId}`}
          title={"결제"}
        />
      </Box>

      <ContentTemplateV2>
        {subscriptionStatus?.expirationAt && (
          <Stack spacing={"12px"}>
            <Stack
              justifyContent="space-between"
              sx={{
                borderRadius: "16px",
                backgroundColor: theme.vars.palette.Surface.Forward,
                p: 1,
              }}
            >
              <Stack
                direction={"row"}
                width={"100%"}
                height={"48px"}
                justifyContent="space-between"
                alignItems="center"
                sx={{ px: "12px" }}
              >
                <CustomTypography
                  variant={"Body"}
                  sx={{
                    fontWeight: 500,
                    color: theme.vars.palette.Content.Medium,
                  }}
                >
                  {"플랜"}
                </CustomTypography>
                {user?.userId && (
                  <CustomTypography
                    variant={"Body"}
                    sx={{
                      fontWeight: 700,
                      color: theme.vars.palette.Content.Strong,
                    }}
                  >
                    {user?.plan === "starter"
                      ? "스타터"
                      : user?.plan === "challenger"
                        ? "챌린저"
                        : user?.plan === "master"
                          ? "마스터"
                          : "챌린저"}
                  </CustomTypography>
                )}
              </Stack>

              {(subscriptionStatus?.bonusDays ?? 0) > 0 && (
                <Stack
                  direction={"row"}
                  width={"100%"}
                  height={"48px"}
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ px: "12px" }}
                >
                  <CustomTypography
                    variant={"Body"}
                    sx={{
                      fontWeight: 500,
                      color: theme.vars.palette.Content.Medium,
                    }}
                  >
                    {"보너스"}
                  </CustomTypography>
                  <CustomTypography
                    variant={"Body"}
                    sx={{
                      fontWeight: 700,
                      color: theme.vars.palette.Content.Strong,
                    }}
                  >
                    {`+${subscriptionStatus?.bonusDays}일`}
                  </CustomTypography>
                </Stack>
              )}

              <Stack
                direction={"row"}
                width={"100%"}
                height={"48px"}
                justifyContent="space-between"
                alignItems="center"
                sx={{ px: "12px" }}
              >
                <CustomTypography
                  variant={"Body"}
                  sx={{
                    fontWeight: 500,
                    color: !isExpired
                      ? theme.vars.palette.Content.Medium
                      : theme.vars.palette.Content.HighlightWithAlert,
                  }}
                >
                  {!isPricingModelC
                    ? !isExpired
                      ? "만료 예정"
                      : "만료됨"
                    : !isExpired
                      ? "집중 코칭 만료 예정"
                      : "집중 코칭 만료됨"}
                </CustomTypography>
                {user?.userId && (
                  <CustomTypography
                    variant={"Body"}
                    sx={{
                      fontWeight: 700,
                      color: !isExpired
                        ? theme.vars.palette.Content.Strong
                        : theme.vars.palette.Content.HighlightWithAlert,
                    }}
                  >
                    {formatDateToKorean(subscriptionStatus?.expirationAt)}
                  </CustomTypography>
                )}
              </Stack>
            </Stack>
            {!isPricingModelC && (
              <Stack spacing={1} px={1}>
                <CustomTypography
                  variant={"Caption"}
                  sx={{
                    color: theme.vars.palette.Content.Weak,
                    fontWeight: 500,
                  }}
                >
                  {"만료 후에도 새 이용권을 구매하여 연장 이용이 가능합니다."}
                </CustomTypography>
                {!isExtensionButtonShown && (
                  <CustomTypography
                    variant={"Caption"}
                    sx={{
                      color: theme.vars.palette.Content.Weak,
                      fontWeight: 500,
                    }}
                  >
                    {"연장 이용권은 만료 예정 20일 전부터 구매 가능합니다."}
                  </CustomTypography>
                )}
              </Stack>
            )}
          </Stack>
        )}

        {!isPricingModelC && isExtensionButtonShown && (
          <>
            <Stack spacing={"12px"} mt={"32px"}>
              <ListButton
                variant={"Soft"}
                color={"primary"}
                text={"연장 이용권 구매하기"}
                endDecorator={
                  <RightArrowSvg color={theme.vars.palette.Content.Highlight} />
                }
                onClick={() => openPayment()}
              />
            </Stack>
            <Stack spacing={1} px={1} mt={"12px"}>
              <CustomTypography
                variant={"Caption"}
                sx={{
                  color: theme.vars.palette.Content.Weak,
                  fontWeight: 500,
                }}
              >
                {
                  "연장 이용의 경우, 기간 내 문제가 해결되어 이용 종료를 원하신다면 남은 기간에 대해 일할 계산하여 환불이 가능합니다."
                }
              </CustomTypography>
            </Stack>
          </>
        )}
        {!isExtensionButtonShown && isPlanUpgradeButtonShown && (
          <>
            <Stack spacing={"12px"} mt={"32px"}>
              <ListButton
                variant={"Soft"}
                color={"primary"}
                text={"플랜 업그레이드하기"}
                endDecorator={
                  <RightArrowSvg color={theme.vars.palette.Content.Highlight} />
                }
                onClick={() => openPlanUpgradePage()}
              />
            </Stack>
            <Stack spacing={1} px={1} mt={"12px"}>
              <CustomTypography
                variant={"Caption"}
                sx={{
                  color: theme.vars.palette.Content.Weak,
                  fontWeight: 500,
                }}
              >
                {
                  "플랜 변경은 최초 결제일로부터 14일 이내 1회에 한해 가능합니다. 다운그레이드는 고객센터로 문의해주세요."
                }
              </CustomTypography>
            </Stack>
          </>
        )}
      </ContentTemplateV2>
    </>
  );
}
